import { useState } from 'react';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { IconCircleCheck } from '@components/icons/circle-check';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDropside } from '@components/icons/dropside';
import { IconRadioIdle } from '@components/icons/radio-idle';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Shimmer } from '@components/shimmer';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import type { PlanTodo, PlanTodoStatus } from './plan';

type PlanTodoItemProps = {
  todo: PlanTodo;
  isFirst?: boolean;
  isLast?: boolean;
};

type StatusIconInnerProps = {
  status: PlanTodoStatus;
};

const TODO_ROW_PADDING_PX = 8;
const TODO_TEXT_LINE_HEIGHT_PX = 20;
const TODO_ICON_SIZE_PX = 16;
const TODO_DROPSIDE_SIZE_PX = 24;
const TODO_ROW_CONTENT_HEIGHT_PX = Math.max(TODO_TEXT_LINE_HEIGHT_PX, TODO_DROPSIDE_SIZE_PX);
const TODO_ROW_PADDING = pxToRem(TODO_ROW_PADDING_PX);
const TODO_ICON_SIZE_REM = pxToRem(TODO_ICON_SIZE_PX);
const TODO_ICON_OFFSET = pxToRem(
  TODO_ROW_PADDING_PX + (TODO_ROW_CONTENT_HEIGHT_PX - TODO_ICON_SIZE_PX) / 2
);

// Inner icon only - rail is handled separately
const StatusIconInner = ({ status }: StatusIconInnerProps) => {
  if (status === 'in_progress') {
    return (
      <InProgressIconWrapper>
        <PendingRing />
        <AbsoluteSpinnerWrapper>
          <SizedRelativeSpinner size={10} $skipMargin $borderWidth={1.5} />
        </AbsoluteSpinnerWrapper>
      </InProgressIconWrapper>
    );
  }

  if (status === 'completed') {
    return <IconCircleCheck color="backgroundPrimary" size={TODO_ICON_SIZE_PX} />;
  }

  if (status === 'cancelled') {
    return <IconCloseRegular size={TODO_ICON_SIZE_PX} color="elementRed100" />;
  }

  return <PendingRing />;
};

const TodoLabel = ({ status, label }: { status: PlanTodoStatus; label: string }) => {
  if (status === 'completed' || status === 'cancelled') {
    return (
      <Text
        font="regular14"
        color="textHelper"
        style={{ flex: 1, textDecoration: 'line-through' }}
        text={label}
      />
    );
  }

  if (status === 'in_progress') {
    return <Shimmer font="regular14" color="textPrimary" style={{ flex: 1 }} text={label} />;
  }

  return <Text font="regular14" color="textPrimary" style={{ flex: 1 }} text={label} />;
};

export const PlanTodoItem = ({ todo, isFirst, isLast }: PlanTodoItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasDescription = Boolean(todo.description);
  const showLineTop = !isFirst;
  const showLineBottom = !isLast;

  const handleClick = () => {
    if (hasDescription) {
      setIsExpanded((prev) => {
        return !prev;
      });
    }
  };

  return (
    <TodoItemContainer
      onClick={handleClick}
      data-has-description={hasDescription}
      aria-expanded={hasDescription ? isExpanded : undefined}
    >
      <TodoRailColumn>
        <TodoRailLineTop $visible={showLineTop} />
        <TodoIconContainer>
          <StatusIconInner status={todo.status} />
        </TodoIconContainer>
        {showLineBottom && <TodoRailLine />}
      </TodoRailColumn>

      <Stack style={{ flex: 1, minWidth: 0 }}>
        <TodoItemRow>
          <TodoLabel status={todo.status} label={todo.label} />
          {hasDescription && (
            <IconDropside
              rotateDegrees={isExpanded ? 90 : 0}
              color="buttonIconPositive"
              size={TODO_DROPSIDE_SIZE_PX}
            />
          )}
        </TodoItemRow>

        {hasDescription && (
          <Collapse isCollapsed={!isExpanded}>
            <DescriptionContent>
              <Text font="regular14" color="textHelper" text={todo.description} />
            </DescriptionContent>
          </Collapse>
        )}
      </Stack>
    </TodoItemContainer>
  );
};

const TodoItemContainer = styled.li({
  display: 'flex',
  alignItems: 'stretch',
  gap: pxToRem(8),
  margin: `0 ${pxToRem(-8)}`,
  borderRadius: pxToRem(6),
  cursor: 'default',

  '&[data-has-description="true"]': {
    cursor: 'pointer',

    '&:hover': {
      background: theme.color.backgroundLightDarkHover,
    },
  },
});

const TodoItemRow = styled(Stack).attrs({
  orientation: 'horizontal',
  space: 2,
  align: 'center',
})({
  flex: 1,
  padding: TODO_ROW_PADDING,
  paddingLeft: 0,
  background: 'transparent',
  textAlign: 'left',
});

const TodoRailColumn = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: TODO_ICON_SIZE_REM,
  flexShrink: 0,
  marginLeft: pxToRem(8),
});

const TodoRailLineTop = styled.div<{ $visible: boolean }>((p) => ({
  width: pxToRem(1),
  height: TODO_ICON_OFFSET,
  backgroundColor: p.$visible ? theme.color.border10 : 'transparent',
}));

const TodoRailLine = styled.div({
  width: pxToRem(1),
  flex: '1 1 auto',
  backgroundColor: theme.color.border10,
  minHeight: pxToRem(4),
});

const TodoIconContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: TODO_ICON_SIZE_REM,
  height: TODO_ICON_SIZE_REM,
  flexShrink: 0,
  zIndex: 1,
  backgroundColor: theme.color.backgroundLightDark,
  borderRadius: '50%',
});

const DescriptionContent = styled.div({
  paddingRight: pxToRem(8),
  paddingBottom: pxToRem(8),
});

const InProgressIconWrapper = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  width: TODO_ICON_SIZE_REM,
  height: TODO_ICON_SIZE_REM,
  flexShrink: 0,
});

const AbsoluteSpinnerWrapper = styled.span({
  position: 'absolute',
});

const PendingRing = styled(IconRadioIdle).attrs({
  size: TODO_ICON_SIZE_PX,
  color: 'textHelper',
})({
  flexShrink: 0,
  transform: 'scale(1.2)',
  transformOrigin: 'center',

  '& path': {
    fillOpacity: 1,
  },
});
